import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { orderBy, isArray } from 'lodash';
import StatusesModel from '../../../../model/Statuses';

import translatePropTypes from '../../../translatePropTypes';
import handleDummyAction from '../../utils/Dummy/handleDummyAction';
import Spinner from '../../utils/Spinner';
import withTranslations from '../../utils/Translations/withTranslations';
import TextField from '../base/TextField';
import TreeModalSelector from './TreeModalSelector';
import TreeSelectorTooltip from './TreeSelectorTooltip/TreeSelectorTooltip';
import styles from './FormTreeModalSelector.scss';
import nodeSortingOptionsSequence from '../../../utils/nodeSortingOptionsSequence';
import { isRootNode } from '../../dataViews/DynamicTree';

const MAX_VISIBLE_NAMES = 2;

@withTranslations
export default class FormTreeModalSelector extends React.PureComponent {
  static propTypes = {
    label: PropTypes.string.isRequired,
    models: PropTypes.array.isRequired,
    selection: PropTypes.oneOfType([
      PropTypes.any,
      PropTypes.arrayOf(PropTypes.any),
    ]),
    viewSelector: PropTypes.oneOfType([
      PropTypes.any,
      PropTypes.arrayOf(PropTypes.any),
    ]),
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    loading: PropTypes.bool,
    errorMessage: PropTypes.string,
    formElement: PropTypes.bool,
    editing: PropTypes.bool,
    children: PropTypes.func.isRequired,
    tooltipPosition: PropTypes.string,
    isRenderOnlySelected: PropTypes.bool,
    isValidationErrorHidden: PropTypes.bool,
    isFormField: PropTypes.bool,
    isOnOverlay: PropTypes.bool,
    noLabel: PropTypes.bool,
    adapter: PropTypes.shape({
      resolveNodeParentId: PropTypes.func,
      resolveNodeId: PropTypes.func,
      resolveNodeTitle: PropTypes.func,
      resolveNodeClassName: PropTypes.func,
      nodeIsLeaf: PropTypes.func,
    }),
    selectionFilter: PropTypes.func,
    defaultValue: PropTypes.any,
    syntheticRootNodeName: PropTypes.string,
    maxVisibleNames: PropTypes.number,
    hasTitle: PropTypes.bool,
    dataCy: PropTypes.string,
    statuses: PropTypes.shape({
      Active: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
      }).isRequired,
      BasicByValue: PropTypes.object.isRequired,
    }),
    ...translatePropTypes,
  };

  static defaultProps = {
    editing: true,
    disabled: false,
    required: false,
    loading: false,
    errorMessage: null,
    isValidationErrorHidden: false,
    selection: null,
    selectionFilter: null,
    tooltipPosition: 'right',
    isRenderOnlySelected: false,
    isFormField: false,
    isOnOverlay: false,
    noLabel: false,
    adapter: {},
    defaultValue: null,
    syntheticRootNodeName: null,
    viewSelector: undefined,
    maxVisibleNames: MAX_VISIBLE_NAMES,
    hasTitle: true,
    statuses: StatusesModel,
    formElement: true,
    dataCy: '',
  };

  state = {
    modalOpen: false,
  };

  componentDidUpdate(prevProps) {
    const {
      input: { value, onChange },
      selection,
      loading,
      defaultValue,
    } = this.props;

    if (
      defaultValue &&
      prevProps.loading !== loading &&
      !selection &&
      (value === '' || value === null)
    ) {
      onChange(defaultValue);
    } else if (
      prevProps.loading !== loading &&
      !selection &&
      value !== '' &&
      value !== null
    ) {
      onChange('');
    }
  }

  handleOpenModal = () => {
    if (!this.props.disabled) this.setState({ modalOpen: true });
  };

  handleChange = selection => {
    if (selection) {
      const {
        selectionFilter,
        input: { onChange },
      } = this.props;
      this.setState({ modalOpen: false });

      if (isArray(selection)) {
        return onChange(
          selectionFilter ? selectionFilter(selection) : selection,
        );
      }

      const [item] = selectionFilter
        ? selectionFilter([selection])
        : [selection];
      onChange(item);
    } else {
      this.setState({ modalOpen: false });
    }
  };

  renderTooltip = () => {
    const {
      models,
      selection,
      tooltipPosition,
      isRenderOnlySelected,
      isFormField,
      adapter,
      syntheticRootNodeName,
    } = this.props;

    return (
      <TreeSelectorTooltip
        adapter={adapter}
        isFormField={isFormField}
        isRenderOnlySelected={isRenderOnlySelected}
        models={models}
        position={tooltipPosition}
        selection={selection}
        syntheticRootNodeName={syntheticRootNodeName}
      />
    );
  };

  resolveNodeTitle = node => {
    const {
      adapter: { resolveNodeTitle },
    } = this.props;

    if (resolveNodeTitle) {
      return resolveNodeTitle(node);
    }

    return node.name;
  };

  resolveTitleWithStatus = item => {
    const { statuses } = this.props;
    if (
      (item.status &&
        (item.status === statuses.Active?.value ||
          item.status === statuses.Published?.value)) ||
      !item.status ||
      isRootNode(item)
    ) {
      return this.resolveNodeTitle(item);
    } else if (!(item.status && item.status === statuses.Active?.value)) {
      return `${this.resolveNodeTitle(item)} ${
        `(${statuses.BasicByValue[item.status]?.name})` || ''
      }`;
    }
  };

  renderTitle = () => {
    const { selection, viewSelector, maxVisibleNames } = this.props;

    const _selection = viewSelector || selection;

    if (!_selection) {
      return '';
    }

    const items = orderBy(
      Array.isArray(_selection) ? _selection : [_selection],
      nodeSortingOptionsSequence.iteratees,
      nodeSortingOptionsSequence.orders,
    );

    if (!items.length) {
      return '';
    }

    const title = items
      .slice(0, maxVisibleNames)
      .map(this.resolveTitleWithStatus);

    if (items.length > maxVisibleNames) {
      title.push('...');
    }

    return title.join(', ');
  };

  get errorMessage() {
    const { meta, isValidationErrorHidden, errorMessage } = this.props;
    const requiredErrorMessage =
      (meta.touched || meta.submitFailed) && meta.error ? meta.error : null;

    return isValidationErrorHidden
      ? null
      : requiredErrorMessage || errorMessage;
  }

  render() {
    const {
      label,
      children,
      models,
      disabled,
      required,
      selection,
      isOnOverlay,
      noLabel,
      loading,
      hasTitle,
      formElement,
      hasClearButton,
      dataCy,
    } = this.props;

    const { modalOpen } = this.state;

    if (loading && !disabled && !modalOpen) {
      return <Spinner bottom inline={false} />;
    }

    return (
      <>
        <div data-cy={dataCy} onClick={this.handleOpenModal}>
          <TextField
            animateTitle={!noLabel}
            disabled={disabled}
            errorMessage={this.errorMessage}
            formElement={formElement}
            hasInputTitle={hasTitle}
            inputProps={{
              onBlur: handleDummyAction,
              role: 'button',
              readOnly: '',
              'data-cy': dataCy,
              className: classnames('text-ellipsis', styles.solidBorder),
            }}
            noLabel={noLabel}
            placeholder={label}
            renderAddon={this.renderTooltip}
            required={required}
            value={this.renderTitle()}
            onChange={handleDummyAction}
          />
        </div>
        {modalOpen && (
          <TreeModalSelector
            hasClearButton={hasClearButton}
            isOnOverlay={isOnOverlay}
            label={label}
            modalOpen={modalOpen}
            models={models}
            selection={selection}
            onChange={this.handleChange}
          >
            {children}
          </TreeModalSelector>
        )}
      </>
    );
  }
}
